import React, { useRef, useEffect, useState } from "react";
import styles from "./EditableImageCanvas.module.css";
import { useEditLock } from "./EditLockContext";

const EditableImageCanvas = ({ imageUrl, editData, onChange }) => {
  const containerRef = useRef();
  const canvasRef = useRef();
  const [isCropping, setIsCropping] = useState(false);
  const [cropStart, setCropStart] = useState(null);
  const [isRotating, setIsRotating] = useState(false);
  const [previewRotation, setPreviewRotation] = useState(0);
  const [isDraggingRotation, setIsDraggingRotation] = useState(false);
  const [previewCrop, setPreviewCrop] = useState(null);
  const [, setControlsLocked] = useState(false);
  const [appliedCrop, setAppliedCrop] = useState(null);
  const [resizingHandle, setResizingHandle] = useState(null);
  const [dragOffset, setDragOffset] = useState({ x: 0, y: 0 });
  const [isDraggingCrop, setIsDraggingCrop] = useState(false);
  const [, setCursorStyle] = useState("default");
  const scaleRef = useRef(1);
  useEffect(() => {
    setAppliedCrop(editData?.crop || null);
  }, [editData?.crop, imageUrl]);
  const { lock, unlock, mode: lockMode } = useEditLock();
  useEffect(() => {
    drawCanvas();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    imageUrl,
    appliedCrop,
    previewCrop,
    isCropping,
    isRotating,
    previewRotation,
  ]);

  const drawCanvas = () => {
    const canvas = canvasRef.current;
    const ctx = canvas.getContext("2d");
    const img = new Image();

    img.onload = () => {
      const crop = appliedCrop || editData?.crop;
      const rotation = isRotating ? previewRotation : editData?.rotation || 0;
      const radians = (rotation * Math.PI) / 180;
      const rotated = Math.abs(rotation % 180) === 90;

      // Zone source à dessiner (dans l'image originale)
      let sx = 0,
        sy = 0,
        sWidth = img.width,
        sHeight = img.height;
      if (crop) {
        sx = crop.x;
        sy = crop.y;
        sWidth = crop.width;
        sHeight = crop.height;
      }

      // Taille du canevas visible (dans le DOM)
      const rect = canvas.getBoundingClientRect();
      const visibleWidth = rect.width;
      const visibleHeight = rect.height;

      // Fixe le canevas réel pour correspondre à l'affichage CSS
      canvas.width = canvas.offsetWidth;
      canvas.height = canvas.offsetHeight;

      // Calcul du fit de l'image à l'intérieur du canvas visible
      const scale = Math.min(
        visibleWidth / (rotated ? sHeight : sWidth),
        visibleHeight / (rotated ? sWidth : sHeight)
      );
      scaleRef.current = scale;
      const drawWidth = sWidth * scale;
      const drawHeight = sHeight * scale;

      // Efface l'ancien dessin
      ctx.clearRect(0, 0, canvas.width, canvas.height);

      // Rotation centrée
      ctx.save();
      ctx.translate(canvas.width / 2, canvas.height / 2);
      ctx.rotate(radians);
      ctx.drawImage(
        img,
        sx,
        sy,
        sWidth,
        sHeight,
        -drawWidth / 2,
        -drawHeight / 2,
        drawWidth,
        drawHeight
      );
      ctx.restore();

      // Affiche la zone de crop si active
      if (previewCrop) {
        ctx.save();
        ctx.fillStyle = "rgba(0, 0, 0, 0.4)";
        ctx.beginPath();
        ctx.rect(0, 0, canvas.width, canvas.height);
        ctx.rect(
          previewCrop.x,
          previewCrop.y,
          previewCrop.width,
          previewCrop.height
        );
        ctx.fill("evenodd");
        ctx.strokeStyle = "#137C8B";
        ctx.lineWidth = 2;
        ctx.strokeRect(
          previewCrop.x,
          previewCrop.y,
          previewCrop.width,
          previewCrop.height
        );

        const handles = getCropHandles(previewCrop);
        handles.forEach(([dir, x, y]) => drawHandle(ctx, x, y));
        ctx.restore();
      }
    };

    img.src = imageUrl;
  };

  const drawHandle = (ctx, x, y, baseSize = 12) => {
    const rect = canvasRef.current.getBoundingClientRect();
    const scaleX = canvasRef.current.width / rect.width;
    const scaleY = canvasRef.current.height / rect.height;
    const scale = (scaleX + scaleY) / 2;

    const size = baseSize / scale;

    ctx.beginPath();
    ctx.rect(x - size / 2, y - size / 2, size, size);
    ctx.fillStyle = "#fff";
    ctx.fill();
    ctx.strokeStyle = "#137C8B";
    ctx.lineWidth = 2;
    ctx.stroke();
  };

  const getCanvasCoordinates = (e) => {
    const rect = canvasRef.current.getBoundingClientRect();
    return {
      x: e.clientX - rect.left,
      y: e.clientY - rect.top,
    };
  };

  const handleMouseDown = (e) => {
    const { x, y } = getCanvasCoordinates(e);
    if (isRotating) {
      setIsDraggingRotation(true);
      updateRotationFromPointer(x, y);
      return;
    }
    if (previewCrop) {
      const handleSize = 12;
      const handles = getCropHandles(previewCrop);
      for (let [dir, hx, hy] of handles) {
        if (Math.abs(x - hx) <= handleSize && Math.abs(y - hy) <= handleSize) {
          setResizingHandle(dir);
          return;
        }
      }
      if (
        x >= previewCrop.x &&
        x <= previewCrop.x + previewCrop.width &&
        y >= previewCrop.y &&
        y <= previewCrop.y + previewCrop.height
      ) {
        setIsDraggingCrop(true);
        setDragOffset({ x: x - previewCrop.x, y: y - previewCrop.y });
        return;
      }
    }
    if (isCropping) {
      setCropStart({ x, y });
    }
  };

  const handleMouseMove = (e) => {
    const { x, y } = getCanvasCoordinates(e);
    if (isRotating && isDraggingRotation) {
      updateRotationFromPointer(x, y);
    } else if (resizingHandle && previewCrop) {
      const newCrop = { ...previewCrop };
      switch (resizingHandle) {
        case "nw":
          newCrop.width += newCrop.x - x;
          newCrop.height += newCrop.y - y;
          newCrop.x = x;
          newCrop.y = y;
          break;
        case "n":
          newCrop.height += newCrop.y - y;
          newCrop.y = y;
          break;
        case "ne":
          newCrop.width = x - newCrop.x;
          newCrop.height += newCrop.y - y;
          newCrop.y = y;
          break;
        case "w":
          newCrop.width += newCrop.x - x;
          newCrop.x = x;
          break;
        case "e":
          newCrop.width = x - newCrop.x;
          break;
        case "sw":
          newCrop.width += newCrop.x - x;
          newCrop.x = x;
          newCrop.height = y - newCrop.y;
          break;
        case "s":
          newCrop.height = y - newCrop.y;
          break;
        case "se":
          newCrop.width = x - newCrop.x;
          newCrop.height = y - newCrop.y;
          break;
        default:
          break;
      }
      setPreviewCrop(newCrop);
    } else if (isDraggingCrop && previewCrop) {
      setPreviewCrop({
        ...previewCrop,
        x: x - dragOffset.x,
        y: y - dragOffset.y,
      });
    } else if (isCropping && cropStart) {
      setPreviewCrop({
        x: Math.min(cropStart.x, x),
        y: Math.min(cropStart.y, y),
        width: Math.abs(x - cropStart.x),
        height: Math.abs(y - cropStart.y),
      });
    }
  };

  const handleMouseUp = () => {
    setIsDraggingRotation(false);
    setCropStart(null);
    setResizingHandle(null);
    setIsDraggingCrop(false);
  };

  const updateRotationFromPointer = (x, y) => {
    const centerX = canvasRef.current.width / 2;
    const centerY = canvasRef.current.height / 2;
    const angleRad = Math.atan2(y - centerY, x - centerX);
    const angleDeg = angleRad * (180 / Math.PI);
    setPreviewRotation(angleDeg);
  };

  const applyCrop = () => {
    if (!previewCrop) return;

    const canvas = canvasRef.current;

    const img = new Image();
    img.onload = () => {
      const crop = appliedCrop || editData?.crop;
      const rotation = isRotating ? previewRotation : editData?.rotation || 0;
      const rotated = Math.abs(rotation % 180) === 90;

      const sWidth = crop ? crop.width : img.width;
      const sHeight = crop ? crop.height : img.height;

      const canvasWidth = canvas.offsetWidth;
      const canvasHeight = canvas.offsetHeight;

      const scale = scaleRef.current;

      const drawWidth = (rotated ? sHeight : sWidth) * scale;
      const drawHeight = (rotated ? sWidth : sHeight) * scale;

      const offsetX = (canvasWidth - drawWidth) / 2;
      const offsetY = (canvasHeight - drawHeight) / 2;

      const realCrop = {
        x: Math.round((previewCrop.x - offsetX) / scale),
        y: Math.round((previewCrop.y - offsetY) / scale),
        width: Math.round(previewCrop.width / scale),
        height: Math.round(previewCrop.height / scale),
      };

      setAppliedCrop(realCrop);
      onChange({ ...editData, crop: realCrop });
      setIsCropping(false);
      setControlsLocked(false);
      setPreviewCrop(null);
      setCursorStyle("default");
      unlock();
    };
    img.src = imageUrl;
  };

  const applyRotation = () => {
    onChange({ ...editData, rotation: previewRotation });
    setIsRotating(false);
    setControlsLocked(false);
    setIsDraggingRotation(false);
    setPreviewRotation(0);
    unlock();
  };

  const cancelAll = () => {
    setIsCropping(false);
    setIsRotating(false);
    setIsDraggingRotation(false);
    setControlsLocked(false);
    setPreviewCrop(null);
    setPreviewRotation(0);
    setCursorStyle("default");
    unlock();
  };

  const handleStartCrop = () => {
    lock("crop");
    setIsCropping(true);
    setIsRotating(false);
    setControlsLocked(true);
    setPreviewCrop(null);
    setAppliedCrop(null);
  };

  const handleStartRotation = () => {
    lock("rotate");
    setIsRotating(true);
    setIsCropping(false);
    setControlsLocked(true);
    setPreviewRotation(editData?.rotation || 0);
    setPreviewCrop(null);
  };

  const getCropHandles = (crop) => [
    ["nw", crop.x, crop.y],
    ["n", crop.x + crop.width / 2, crop.y],
    ["ne", crop.x + crop.width, crop.y],
    ["w", crop.x, crop.y + crop.height / 2],
    ["e", crop.x + crop.width, crop.y + crop.height / 2],
    ["sw", crop.x, crop.y + crop.height],
    ["s", crop.x + crop.width / 2, crop.y + crop.height],
    ["se", crop.x + crop.width, crop.y + crop.height],
  ];

  return (
    <div
      ref={containerRef}
      style={{ height: "100%", display: "flex", flexDirection: "column" }}>
      <div style={{ flex: 1, overflow: "auto" }}>
        <div className={styles.canvasWrapper}>
          <canvas
            ref={canvasRef}
            onMouseDown={handleMouseDown}
            onMouseMove={handleMouseMove}
            onMouseUp={handleMouseUp}
          />
        </div>
      </div>
      <div className={styles.controls}>
        <button
          onClick={() =>
            !isRotating ? handleStartRotation() : applyRotation()
          }
          disabled={lockMode === "crop"}>
          {isRotating ? "✅ Appliquer" : "⟳ Rotation"}
        </button>
        <button
          onClick={() => (!isCropping ? handleStartCrop() : applyCrop())}
          disabled={lockMode === "rotate"}>
          {isCropping ? "✅ Appliquer" : "✂️ Crop"}
        </button>
        <button
          onClick={() =>
            isCropping || isRotating
              ? cancelAll()
              : onChange({
                  ...editData,
                  crop: null,
                  rotation: 0,
                  textOverlays: [],
                })
          }>
          {isCropping || isRotating ? "❌ Annuler" : "❌ Réinitialiser"}
        </button>
      </div>
    </div>
  );
};

export default EditableImageCanvas;
